import { Component } from '@theme/component';

// API Configuration
const API_BASE_URL = 'https://360.danzia.com/api';
const BUNDLE_ORDER_ENDPOINT = `${API_BASE_URL}/create-bundle-order`;

// Gift Message Fee (in dollars)
const GIFT_MESSAGE_FEE = 5.00;

/**
 * Bundle Builder Component
 * 
 * Handles product filtering by category, bundle management, and checkout.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} productCards - The product card elements.
 * @property {HTMLElement[]} categoryFilters - The category filter buttons.
 * @property {HTMLElement[]} [subcategoryFilters] - The secondary filter containers (optional).
 * @property {HTMLElement} selectedItemsList - The selected items list container.
 * @property {HTMLElement} selectedCount - The selected count text element.
 * @property {HTMLElement} subtotalAmount - The subtotal amount element.
 * @property {HTMLElement} discountAmount - The discount amount element.
 * @property {HTMLElement} discountRow - The discount row element.
 * @property {HTMLElement} totalAmount - The total amount element.
 * @property {HTMLElement} checkoutButton - The checkout button element.
 * @property {HTMLElement} progressFill - The progress bar fill element.
 * @property {HTMLElement} progressText - The progress bar text element.
 * @property {HTMLElement} [bundleError] - The error message element (optional).
 *
 * @extends {Component<Refs>}
 */
class BundleBuilderComponent extends Component {
  /** @type {Map<string, object>} */
  #selectedProducts = new Map();

  /** @type {Array<{items: number, discount: number}>} */
  #discountTiers = [];

  /** @type {string} */
  #activeCategory = 'all';

  /** @type {Map<string, string>} */
  #activeSubcategories = new Map();

  connectedCallback() {
    super.connectedCallback();

    // Get discount tiers from discountTiers ref element's data attribute
    if (this.refs.discountTiers && this.refs.discountTiers.dataset.tiers) {
      try {
        const parsedTiers = JSON.parse(this.refs.discountTiers.dataset.tiers);

        this.#discountTiers = Array.isArray(parsedTiers)
          ? parsedTiers
              .map((tier) => ({
                items: Number(tier.items),
                discount: Number(tier.discount),
              }))
              .filter(
                (tier) =>
                  Number.isFinite(tier.items) &&
                  tier.items > 0 &&
                  Number.isFinite(tier.discount)
              )
              .sort((a, b) => a.items - b.items)
          : [];
      } catch (e) {
        console.error('Failed to parse discount tiers:', e);
        this.#discountTiers = [];
      }
    } else {
      this.#discountTiers = [];
    }

    // Update tier displays from metaobject data
    this.#updateTierDisplays();

    // Load saved bundle from localStorage
    this.#loadFromStorage();

    // Initial UI update
    this.#updateUI();

    // MANUAL EVENT BINDING
    this.#bindManualEvents();

    // Setup mobile sidebar toggle
    this.#setupMobileToggle();

  }

  /**
   * Update tier displays from metaobject data
   */
  #updateTierDisplays() {
    if (this.refs.tierCount && this.refs.tierDiscount) {
      this.#discountTiers.forEach((tier, index) => {
        if (this.refs.tierCount[index]) {
          this.refs.tierCount[index].textContent = tier.items;
        }
        if (this.refs.tierDiscount[index]) {
          this.refs.tierDiscount[index].textContent = `${tier.discount}% OFF`;
        }
      });
    }

    // Update initial progress text
    if (this.refs.progressHighlight && this.#discountTiers[0]) {
      this.refs.progressHighlight.textContent = `${this.#discountTiers[0].items} items = ${this.#discountTiers[0].discount}% OFF`;
    }
  }

  /**
   * Manually bind events as fallback
   */
  #bindManualEvents() {
    // Bind category filters (only once)
    if (this.refs.categoryFilters && !this._categoryFiltersBound) {
      this.refs.categoryFilters.forEach((filter) => {
        filter.addEventListener('click', (e) => this.handleCategoryFilter(e));
      });
      this._categoryFiltersBound = true;
    }

    // Bind secondary filter pills (only once)
    if (this.refs.subcategoryFilters && !this._subcategoryFiltersBound) {
      this.refs.subcategoryFilters.forEach((container) => {
        container.querySelectorAll('.subcategory-filter').forEach((button) => {
          button.addEventListener('click', (e) => this.handleSubcategoryFilter(e));
        });
      });
      this._subcategoryFiltersBound = true;
    }

    // Bind product add buttons
    if (this.refs.productCards) {
      this.refs.productCards.forEach((card) => {
        const button = card.querySelector('.btn-add-to-bundle');
        if (button && !button._eventBound) {
          button.addEventListener('click', (e) => this.handleProductToggle(e));
          button._eventBound = true;
        }
      });
    }

    // Bind checkout button (only once)
    if (this.refs.checkoutButton && !this._checkoutButtonBound) {
      this.refs.checkoutButton.addEventListener('click', (e) => this.handleCheckout(e));
      this._checkoutButtonBound = true;
    }
    
    // Bind gift checkbox (only once)
    if (this.refs.giftCheckbox && !this._giftCheckboxBound) {
      this.refs.giftCheckbox.addEventListener('change', () => this.handleGiftToggle());
      this._giftCheckboxBound = true;
    }
  }

  /**
   * Handles category filter selection.
   */
  handleCategoryFilter(event) {
    if (!this.refs.productCards) return;

    const filter = event.currentTarget;
    const category = filter.dataset.category;
    const previousCategory = this.#activeCategory;

    this.#activeCategory = category;

    // Reset the secondary filter when entering a different category
    if (previousCategory !== category) {
      this.#activeSubcategories.set(category, 'all');
    }

    // Update active state
    if (this.refs.categoryFilters) {
      this.refs.categoryFilters.forEach((f) => f.classList.remove('active'));
    }
    filter.classList.add('active');

    this.#syncSubcategoryFilters();
    this.#applyFilters();

    // Smooth scroll on mobile
    if (window.innerWidth <= 768 && this.refs.productsGrid) {
      this.refs.productsGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Handles secondary filter selection.
   */
  handleSubcategoryFilter(event) {
    const button = event.currentTarget;
    const container = button.closest('.subcategory-filters');

    if (!container) return;

    const category = container.dataset.category;
    this.#activeSubcategories.set(category, button.dataset.subcategory);

    this.#syncSubcategoryFilters();
    this.#applyFilters();
  }

  /**
   * Shows the secondary filter that belongs to the active category and syncs its active pill.
   */
  #syncSubcategoryFilters() {
    if (!this.refs.subcategoryFilters) return;

    this.refs.subcategoryFilters.forEach((container) => {
      const isActiveCategory =
        this.#activeCategory !== 'all' &&
        container.dataset.category.toLowerCase() === this.#activeCategory.toLowerCase();

      container.hidden = !isActiveCategory;

      if (!isActiveCategory) return;

      const activeSubcategory = this.#activeSubcategories.get(this.#activeCategory) || 'all';

      container.querySelectorAll('.subcategory-filter').forEach((button) => {
        const isActive = button.dataset.subcategory === activeSubcategory;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    });
  }

  /**
   * Applies the active category and secondary filter to the product cards.
   */
  #applyFilters() {
    if (!this.refs.productCards) return;

    const activeCategory = this.#activeCategory;
    const activeSubcategory =
      activeCategory === 'all' ? 'all' : this.#activeSubcategories.get(activeCategory) || 'all';

    this.refs.productCards.forEach((card) => {
      const cardCategory = (card.dataset.category || '').toLowerCase();
      const matchesCategory =
        activeCategory === 'all' || cardCategory === activeCategory.toLowerCase();
      const matchesSubcategory =
        activeSubcategory === 'all' || card.dataset.subcategory === activeSubcategory;

      card.style.display = matchesCategory && matchesSubcategory ? '' : 'none';
    });

    this.querySelectorAll('.bundle-category-empty').forEach((emptyState) => {
      const emptyCategory = (emptyState.dataset.category || '').toLowerCase();
      const matchesCategory =
        activeCategory === 'all' || emptyCategory === activeCategory.toLowerCase();

      emptyState.style.display = matchesCategory ? '' : 'none';
    });
  }

  /**
   * Handles product toggle (add/remove from bundle).
   * @param {MouseEvent} event
   */
  handleProductToggle(event) {

    const button = event.currentTarget;
    const productCard = button.closest('.bundle-product-card');

    if (!productCard) {
      console.warn('Product card not found');
      return;
    }

    const productId = productCard.dataset.productId;
    const isSelected = this.#selectedProducts.has(productId);

    if (isSelected) {
      this.#removeProduct(productId);
    } else {
      const productData = {
        id: productId,
        title: productCard.dataset.productTitle,
        price: parseFloat(productCard.dataset.productPrice),
        image: productCard.dataset.productImage,
        variantId: productCard.dataset.variantId,
      };
      this.#addProduct(productId, productData);
    }
  }

  /**
   * Adds a product to the bundle.
   * @param {string} productId
   * @param {object} productData
   */
  #addProduct(productId, productData) {
    // Check if product already exists
    if (this.#selectedProducts.has(productId)) {
      // If exists, increase quantity
      this.#increaseQuantity(productId);
      return;
    }

    // Add new product with quantity 1
    productData.quantity = 1;
    this.#selectedProducts.set(productId, productData);
    this.#updateUI();
    this.#saveToStorage();


    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent('bundle:product-added', {
        detail: { productId, productData },
        bubbles: true,
      })
    );
  }

  /**
   * Increase product quantity
   * @param {string} productId
   */
  #increaseQuantity(productId) {
    const product = this.#selectedProducts.get(productId);
    if (product) {
      product.quantity = (product.quantity || 1) + 1;
      this.#updateUI();
      this.#saveToStorage();
    }
  }

  /**
   * Decrease product quantity
   * @param {string} productId
   */
  #decreaseQuantity(productId) {
    const product = this.#selectedProducts.get(productId);
    if (product) {
      if (product.quantity > 1) {
        product.quantity -= 1;
        this.#updateUI();
        this.#saveToStorage();
      } else {
        // If quantity is 1, remove the product
        this.#removeProduct(productId);
      }
    }
  }

  /**
   * Removes a product from the bundle.
   * @param {string} productId
   */
  #removeProduct(productId) {
    const productData = this.#selectedProducts.get(productId);
    this.#selectedProducts.delete(productId);
    this.#updateUI();
    this.#saveToStorage();


    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent('bundle:product-removed', {
        detail: { productId },
        bubbles: true,
      })
    );
  }

  /**
   * Calculates the current discount percentage.
   * @returns {number}
   */
  #calculateDiscount() {
    // Calculate total quantity of all products
    let totalQuantity = 0;
    this.#selectedProducts.forEach((p) => {
      totalQuantity += p.quantity || 1;
    });
    
    const tier = [...this.#discountTiers].reverse().find((t) => totalQuantity >= t.items);
    return tier ? tier.discount : 0;
  }

  /**
   * Calculates bundle totals.
   * @returns {{subtotal: number, discount: number, discountAmount: number, giftFee: number, total: number, count: number}}
   */
  #calculateTotal() {
    let subtotal = 0;
    let totalItems = 0;

    this.#selectedProducts.forEach((p) => {
      const quantity = p.quantity || 1;
      subtotal += parseFloat(p.price) * quantity;
      totalItems += quantity;
    });

    const discount = this.#calculateDiscount();
    const discountAmount = subtotal * (discount / 100);
    
    // Add gift fee if checkbox is checked (convert dollars to cents to match product prices)
    const giftFee = (this.refs.giftCheckbox?.checked) ? (GIFT_MESSAGE_FEE * 100) : 0;

    return {
      subtotal,
      discount,
      discountAmount,
      giftFee,
      total: subtotal - discountAmount + giftFee,
      count: totalItems,
    };
  }

  /**
   * Updates all UI elements.
   */
  #updateUI() {
    this.#updateProductCards();
    this.#updateSidebar();
    this.#updateProgressBar();
  }

  /**
   * Updates product card visual states.
   */
  #updateProductCards() {
    // Fallback for legacy refs structure
    if (!this.refs.productCards) return;

    this.refs.productCards.forEach((card) => {
      const productId = card.dataset.productId;
      const isSelected = this.#selectedProducts.has(productId);

      card.classList.toggle('selected', isSelected);

      const button = card.querySelector('.btn-add-to-bundle');
      if (button) {
        const addText = button.querySelector('.btn-text-add');
        const removeText = button.querySelector('.btn-text-remove');
        const overlay = card.querySelector('.selected-overlay');

        if (addText && removeText) {
          addText.style.display = isSelected ? 'none' : 'flex';
          removeText.style.display = isSelected ? 'flex' : 'none';
        }

        if (overlay) {
          overlay.style.display = isSelected ? 'flex' : 'none';
        }
      }
    });
  }

  /**
   * Updates the bundle sidebar.
   */
  #updateSidebar() {
    const totals = this.#calculateTotal();

    // Update selected items list
    if (this.refs.selectedItemsList) {
      if (this.#selectedProducts.size === 0) {
        // Show empty state
        this.refs.selectedItemsList.innerHTML = `
          <div class="empty-bundle-message">
            <svg class="empty-icon" width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="30" stroke="currentColor" stroke-width="2" stroke-dasharray="4 4"/>
              <path d="M32 20v24M20 32h24" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <p>Start building your bundle</p>
            <p class="empty-subtext">Select kits above to get started</p>
          </div>
        `;
      } else {
        // Show selected items
        this.refs.selectedItemsList.innerHTML = '';
        this.#selectedProducts.forEach((product, id) => {
          const quantity = product.quantity || 1;
          const itemEl = document.createElement('div');
          itemEl.className = 'selected-item';
          itemEl.innerHTML = `
            <img src="${product.image}" alt="${product.title}" class="selected-item-image">
            <div class="selected-item-info">
              <p class="selected-item-title">${product.title}</p>
              <div class="selected-item-bottom">
                <p class="selected-item-price">${this.#formatMoney(product.price * quantity)}</p>
                <div class="quantity-controls">
                  <button 
                    class="btn-quantity-decrease" 
                    data-product-id="${id}"
                    aria-label="Decrease quantity"
                    type="button"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor">
                      <path d="M2 6h8" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                  <span class="quantity-value">${quantity}</span>
                  <button 
                    class="btn-quantity-increase" 
                    data-product-id="${id}"
                    aria-label="Increase quantity"
                    type="button"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor">
                      <path d="M6 2v8M2 6h8" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <button 
              class="btn-remove-item" 
              data-product-id="${id}"
              aria-label="Remove ${product.title}"
              type="button"
            >×</button>
          `;

          // Manually bind remove button click event
          const removeBtn = itemEl.querySelector('.btn-remove-item');
          if (removeBtn) {
            removeBtn.addEventListener('click', () => {
              this.#removeProduct(id);
            });
          }

          // Bind quantity decrease button
          const decreaseBtn = itemEl.querySelector('.btn-quantity-decrease');
          if (decreaseBtn) {
            decreaseBtn.addEventListener('click', () => {
              this.#decreaseQuantity(id);
            });
          }

          // Bind quantity increase button
          const increaseBtn = itemEl.querySelector('.btn-quantity-increase');
          if (increaseBtn) {
            increaseBtn.addEventListener('click', () => {
              this.#increaseQuantity(id);
            });
          }

          this.refs.selectedItemsList.appendChild(itemEl);
        });
      }
    }

    // Update count
    if (this.refs.selectedCount) {
      const countBadge = this.refs.selectedCount.querySelector('.count-badge');
      if (countBadge) {
        countBadge.textContent = totals.count;
      }
    }

    // Update totals
    if (this.refs.subtotalAmount) {
      this.refs.subtotalAmount.textContent = this.#formatMoney(totals.subtotal);
    }

    if (this.refs.discountAmount) {
      this.refs.discountAmount.textContent = `-${this.#formatMoney(totals.discountAmount)}`;
    }

    if (this.refs.discountRow) {
      this.refs.discountRow.style.display = totals.discount > 0 ? 'flex' : 'none';
    }

    if (this.refs.giftFeeRow) {
      this.refs.giftFeeRow.style.display = totals.giftFee > 0 ? 'flex' : 'none';
    }

    if (this.refs.totalAmount) {
      this.refs.totalAmount.textContent = this.#formatMoney(totals.total);
    }

    // Update checkout button
    if (this.refs.checkoutButton) {
      this.refs.checkoutButton.disabled = totals.count === 0;
      const btnText = this.refs.checkoutButton.querySelector('.btn-text');
      if (btnText) {
        btnText.textContent = `Checkout Bundle • ${this.#formatMoney(totals.total)}`;
      }
    }
    
    // Update mobile summary if it exists
    this.#updateMobileSummary();
  }

  /**
   * Updates the discount progress bar.
   */
  #updateProgressBar() {
    const totals = this.#calculateTotal();
    const currentCount = totals.count;
    const nextTier = this.#discountTiers.find((t) => currentCount < t.items);

    // Update tier visual states
    this.#updateTierStates(currentCount);

    const progressTrack = this.refs.progressFill?.parentElement;
    const progressText = this.refs.progressText;
    const progressHighlight = progressText?.querySelector('.progress-highlight');

    if (nextTier) {
      const progress = Math.min((currentCount / nextTier.items) * 100, 100);
      const remaining = nextTier.items - currentCount;
      const isAlmostUnlocked = remaining === 1;

      if (this.refs.progressFill) {
        this.refs.progressFill.style.width = `${progress}%`;

        // Add almost unlocked effect
        if (isAlmostUnlocked) {
          this.refs.progressFill.classList.add('almost-unlocked');
        } else {
          this.refs.progressFill.classList.remove('almost-unlocked');
        }
      }

      if (progressTrack) {
        if (isAlmostUnlocked) {
          progressTrack.classList.add('almost-unlocked');
        } else {
          progressTrack.classList.remove('almost-unlocked');
        }
      }

      if (progressText) {
        if (isAlmostUnlocked) {
          progressText.classList.add('almost-unlocked');
        } else {
          progressText.classList.remove('almost-unlocked');
        }
      }

      if (progressHighlight) {
        if (remaining > 0) {
          progressHighlight.textContent = `Add ${remaining} more to get ${nextTier.discount}% OFF`;

          if (isAlmostUnlocked) {
            progressHighlight.classList.add('almost-unlocked');
            progressHighlight.textContent = `Add 1 more to unlock ${nextTier.discount}% OFF!`;
          } else {
            progressHighlight.classList.remove('almost-unlocked');
          }
        } else {
          progressHighlight.textContent = `${nextTier.discount}% OFF unlocked!`;
          progressHighlight.classList.remove('almost-unlocked');
        }
      }
    } else {
      // Max tier reached - remove all almost-unlocked effects
      if (this.refs.progressFill) {
        this.refs.progressFill.style.width = '100%';
        this.refs.progressFill.classList.remove('almost-unlocked');
      }

      if (progressTrack) {
        progressTrack.classList.remove('almost-unlocked');
      }

      if (progressText) {
        progressText.classList.remove('almost-unlocked');
      }

      if (progressHighlight) {
        progressHighlight.textContent = `Maximum discount unlocked! ${totals.discount}% OFF`;
        progressHighlight.classList.remove('almost-unlocked');
      }
    }
  }

  /**
   * Update tier visual states and trigger celebrations
   */
  #updateTierStates(currentCount) {
    const tierElements = document.querySelectorAll('.tier-item');
    let justUnlocked = false;

    this.#discountTiers.forEach((tier, index) => {
      const tierElement = tierElements[index];
      if (!tierElement) return;

      const wasActive = tierElement.classList.contains('active');
      const wasUnlocked = tierElement.classList.contains('unlocked');

      // Remove all states first
      tierElement.classList.remove('active', 'unlocked');

      if (currentCount >= tier.items) {
        if (!wasUnlocked && !wasActive) {
          // Just unlocked this tier
          tierElement.classList.add('active');
          justUnlocked = true;

          // Trigger celebration after animation
          setTimeout(() => {
            tierElement.classList.remove('active');
            tierElement.classList.add('unlocked');
          }, 600);
        } else {
          // Already unlocked
          tierElement.classList.add('unlocked');
        }
      }
    });

    return justUnlocked;
  }

  /**
   * Handles checkout button click.
   * Sends product IDs to server API.
   * @param {MouseEvent} event
   */
  async handleCheckout(event) {
    event.preventDefault();

    if (this.#selectedProducts.size === 0) return;

    const button = this.refs.checkoutButton;
    if (!button) return;

    const originalText = button.innerHTML;

    // Show loading state
    button.disabled = true;
    button.innerHTML = '<span class="spinner"></span> Creating order...';

    // Prepare minimal payload - IDs, variant IDs, and quantities
    const products = Array.from(this.#selectedProducts.values()).map(p => ({
      id: p.id,
      variantId: p.variantId,
      quantity: p.quantity || 1
    }));

    const payload = {
      products: products
    };
    
    // Add gift data if checkbox is checked
    if (this.refs.giftCheckbox?.checked) {
      payload.isGift = true;
      payload.giftMessage = this.refs.giftMessage?.value || '';
    }


    try {
      const response = await fetch(BUNDLE_ORDER_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.checkoutUrl) {
        // Clear storage and reset form before redirect
        this.#clearStorage();
        this.#resetGiftForm();
        window.location.href = data.checkoutUrl;
      } else {
        console.error('❌ Checkout failed:', data.message);
        this.#showError(data.message || 'Failed to create order. Please try again.');
        button.disabled = false;
        button.innerHTML = originalText;
      }
    } catch (error) {
      console.error('❌ Checkout error:', error);
      this.#showError('Failed to create order. Please try again.');
      button.disabled = false;
      button.innerHTML = originalText;
    }
  }

  /**
   * Handles remove item from bundle.
   * @param {MouseEvent} event
   */
  handleRemoveItem(event) {
    const button = event.currentTarget;
    const productId = button.dataset.productId;

    if (productId) {
      this.#removeProduct(productId);
    }
  }
  
  /**
   * Handles gift checkbox toggle
   */
  handleGiftToggle() {
    const isChecked = this.refs.giftCheckbox?.checked || false;
    
    // Show/hide gift message textarea
    if (this.refs.giftMessageWrapper) {
      this.refs.giftMessageWrapper.style.display = isChecked ? 'block' : 'none';
    }
    
    // Clear message if unchecked
    if (!isChecked && this.refs.giftMessage) {
      this.refs.giftMessage.value = '';
    }
    
    // Update totals to include gift fee
    this.#updateSidebar();
  }
  
  /**
   * Reset gift form to default state
   */
  #resetGiftForm() {
    // Uncheck gift checkbox
    if (this.refs.giftCheckbox) {
      this.refs.giftCheckbox.checked = false;
    }
    
    // Clear gift message
    if (this.refs.giftMessage) {
      this.refs.giftMessage.value = '';
    }
    
    // Hide gift message wrapper
    if (this.refs.giftMessageWrapper) {
      this.refs.giftMessageWrapper.style.display = 'none';
    }
  }

  /**
   * Shows an error message.
   * @param {string} message
   */
  #showError(message) {
    if (this.refs.bundleError) {
      this.refs.bundleError.textContent = message;
      this.refs.bundleError.style.display = 'block';

      setTimeout(() => {
        if (this.refs.bundleError) {
          this.refs.bundleError.style.display = 'none';
        }
      }, 5000);
    }
  }

  /**
   * Formats money value.
   * @param {number} cents - Amount in cents
   * @returns {string}
   */
  #formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Saves selected products to localStorage.
   */
  #saveToStorage() {
    try {
      const data = Array.from(this.#selectedProducts.entries());
      localStorage.setItem('bundle-selected-products', JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save to localStorage:', e);
    }
  }

  /**
   * Loads selected products from localStorage.
   */
  #loadFromStorage() {
    try {
      const data = localStorage.getItem('bundle-selected-products');
      if (data) {
        const entries = JSON.parse(data);
        this.#selectedProducts = new Map(entries);
      }
    } catch (e) {
      console.warn('Failed to load from localStorage:', e);
    }
  }

  /**
   * Clears localStorage.
   */
  #clearStorage() {
    try {
      localStorage.removeItem('bundle-selected-products');
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
  }

  /**
   * Setup mobile sidebar toggle functionality
   */
  #setupMobileToggle() {
    // Only setup on mobile
    if (window.innerWidth > 768) return;
    if (!this.refs.bundleSidebar || !this.refs.sidebarSticky) return;

    // Check if already setup
    if (this.refs.bundleSidebar.querySelector('.mobile-bundle-summary')) return;

    // Create mobile summary header
    const summaryHeader = document.createElement('div');
    summaryHeader.className = 'mobile-bundle-summary';
    summaryHeader.innerHTML = `
      <div class="mobile-summary-content">
        <span class="mobile-item-count">0 items</span>
        <span class="mobile-total-price">$0.00</span>
      </div>
      <svg class="mobile-expand-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    `;

    // Wrap sidebar content
    const detailsWrapper = document.createElement('div');
    detailsWrapper.className = 'mobile-bundle-details';
    
    // Move all children of bundleSidebar into detailsWrapper
    while (this.refs.bundleSidebar.firstChild) {
      detailsWrapper.appendChild(this.refs.bundleSidebar.firstChild);
    }

    // Add summary and details to sidebar
    this.refs.bundleSidebar.appendChild(summaryHeader);
    this.refs.bundleSidebar.appendChild(detailsWrapper);

    // Add click handler for toggle
    summaryHeader.addEventListener('click', () => {
      const isExpanded = summaryHeader.classList.contains('expanded');
      summaryHeader.classList.toggle('expanded');
      detailsWrapper.classList.toggle('expanded');
    });

    // Initial update
    this.#updateMobileSummary();
  }

  /**
   * Update mobile summary header
   */
  #updateMobileSummary() {
    const summaryHeader = this.refs.bundleSidebar?.querySelector('.mobile-bundle-summary');
    if (!summaryHeader) return;

    const totals = this.#calculateTotal();
    const countElement = summaryHeader.querySelector('.mobile-item-count');
    const totalElement = summaryHeader.querySelector('.mobile-total-price');

    if (countElement) {
      countElement.textContent = `${totals.count} item${totals.count !== 1 ? 's' : ''}`;
    }

    if (totalElement) {
      totalElement.textContent = `$${(totals.total / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}